import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { ArrowLeft, Send } from "lucide-react";

export default function Chat() {
  const { matchId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [partner, setPartner] = useState(null);
  const endRef = useRef();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/messages/${matchId}`);
      setMsgs(data);
    } catch (err) {
      console.error("Failed to load messages", err);
    }
  }, [matchId]);

  useEffect(() => {
    (async () => {
      try {
        const { data: matches } = await api.get("/matches");
        const m = matches.find((x) => x.match_id === matchId);
        if (m) setPartner(m.user);
        await load();
      } catch (err) {
        console.error("Failed to init chat", err);
      }
    })();
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, [matchId, load]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    const t = text.trim();
    setText("");
    try {
      const { data } = await api.post("/messages", { match_id: matchId, text: t });
      setMsgs((prev) => [...prev, data]);
    } catch (err) {
      console.error("Failed to send message", err);
      setText(t); // restore text so user can retry
    }
  };

  return (
    <div className="app-shell bg-aurora min-h-screen flex flex-col" data-testid="chat-page">
      {/* Header */}
      <div className="sticky top-0 z-10 backdrop-blur-xl bg-white/80 border-b border-white/60 px-5 py-4 flex items-center gap-3">
        <button onClick={() => nav("/matches")} className="text-[#8A7E91]" data-testid="chat-back"><ArrowLeft size={20} /></button>
        <div className="w-10 h-10 rounded-full overflow-hidden bg-[#EFE0E5] shrink-0">
          {partner?.photo_url && <img src={partner.photo_url} alt="" className="w-full h-full object-cover" />}
        </div>
        <div>
          <p className="font-display text-lg text-[#332D38] leading-tight">{partner?.name || "..."}</p>
          <p className="text-xs text-[#082A24]">✨ Match</p>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3" data-testid="chat-messages">
        {msgs.length === 0 && (
          <div className="text-center text-[#8A7E91] text-sm py-10">Napiš první zprávu a začněte se poznávat 💕</div>
        )}
        {msgs.map((m) => {
          const mine = m.sender_id === user?.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] px-4 py-2.5 text-sm leading-relaxed shadow-sm ${mine ? "bg-gradient-to-br from-[#082A24] to-[#0F3D34] text-white rounded-2xl rounded-br-md" : "bg-white text-[#332D38] rounded-2xl rounded-bl-md"}`}>
                {m.text}
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      {/* Composer */}
      <form onSubmit={send} className="p-4 bg-white/60 backdrop-blur-xl border-t border-white/60 flex gap-2 items-center">
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Napiš zprávu..." className="input-field flex-1" data-testid="chat-input" />
        <button type="submit" className="w-12 h-12 rounded-full bg-gradient-to-br from-[#082A24] to-[#0F3D34] text-white flex items-center justify-center shadow-md active:scale-95" data-testid="chat-send">
          <Send size={18} />
        </button>
      </form>
    </div>
  );
}
